import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { downloadAccountsFile, getTaxSummary } from "@/lib/api";
import { Empty, Stat, TH, TR, errMsg, money } from "@/components/accounts/shared";

// GST / VAT figures per legal entity for the month (prepared, not filed), and the CA pack download.
export default function TaxTab({ month, country, gym }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    setRows(null);
    getTaxSummary(month).then(setRows).catch((e) => toast.error(errMsg(e, "Could not load tax")));
  }, [month]);
  if (!rows) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  const visible = rows.filter((r) => (!country || r.entity.country === country) && (!gym || r.entity.facility_ids.includes(gym)));
  if (!visible.length) return <div className="card-surface"><Empty>No entities match.</Empty></div>;
  return (
    <div className="space-y-4" data-testid="accounts-tax">
      <div className="text-[11px] text-zinc-500">Figures to prepare returns from, not a filing. Output tax is the tax inside money collected in the month less the share of refunds (same as the Dashboard's GST). Set GSTIN / TRN and which gyms belong to each entity in Settings.</div>
      {visible.map(({ entity: e, totals: t, gyms, by_rate, notes }) => (
        <div key={e.id} className="card-surface p-4 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-zinc-100">{e.name} {e.unconfigured && <span className="text-[10px] text-amber-300 ml-1">not set up as an entity</span>}</div>
              <div className="text-xs text-zinc-500">{e.tax_name} {e.tax_id || "(no number set)"}{e.state ? ` · ${e.state}` : ""} · {gyms.map((g) => g.gym).join(", ")}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => downloadAccountsFile("/accounts/ca-pack", { entity_id: e.id, month }, `CA-pack-${e.name}-${month}.xlsx`)}
              className="h-8 border-[#262626] bg-transparent" data-testid="ca-pack"><Download className="h-3.5 w-3.5 mr-1" /> CA pack (.xlsx)</Button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
            <Stat label="Taxable value" value={money(e, t.taxable_value)} sub="ex tax, net of refunds" />
            <Stat label="Output tax" value={money(e, t.net_output_tax)} sub={`${money(e, t.output_tax)} − ${money(e, t.refund_tax)} on refunds`} />
            {"cgst" in t ? <Stat label="CGST + SGST" value={`${money(e, t.cgst)} + ${money(e, t.sgst)}`} sub="intra-state, IGST 0" /> : <Stat label={e.tax_name} value={money(e, t.net_output_tax)} />}
            <Stat label="Input tax" value={money(e, t.input_tax)} sub="bills & purchases" />
            <Stat label="Net payable" value={money(e, t.payable)} />
          </div>
          <table className="w-full text-sm">
            <thead><tr className={TH}><th className="px-3 py-2 font-medium">Gym</th><th className="px-3 py-2 font-medium">Rate</th>
              <th className="px-3 py-2 font-medium text-right">Collected (incl. tax)</th><th className="px-3 py-2 font-medium text-right">Taxable value</th>
              <th className="px-3 py-2 font-medium text-right">Output tax</th><th className="px-3 py-2 font-medium text-right">Input tax</th></tr></thead>
            <tbody>{gyms.map((g) => (
              <tr key={g.facility_id} className={TR}>
                <td className="px-3 py-2 text-zinc-300">{g.gym}</td><td className="px-3 py-2 text-zinc-400">{g.enabled ? `${g.tax_name} ${Math.round(g.rate * 1000) / 10}%` : "no tax"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(e, g.gross_collected)}</td><td className="px-3 py-2 text-right tabular-nums">{money(e, g.taxable_value)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(e, g.net_output_tax, true)}</td><td className="px-3 py-2 text-right tabular-nums">{money(e, g.input_tax, true)}</td>
              </tr>))}</tbody>
          </table>
          <div className="text-[11px] text-zinc-500">By rate: {by_rate.map((r) => `${r.rate}: ${money(e, r.taxable_value)} → ${money(e, r.tax, true)}`).join(" · ")}</div>
          {notes.map((n) => <div key={n} className="text-[11px] text-amber-200/80">{n}</div>)}
        </div>
      ))}
    </div>
  );
}
